import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Loader2,
  ExternalLink,
  MapPin,
  Clock,
  Gauge,
  Heart,
  HeartPulse,
  Flame,
  Mountain,
  Timer,
  Activity,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { fetchStravaActivityWithStreams } from '../utils/stravaActivityApi';
import { useLanguage } from '../contexts/LanguageContext';
import { getActivityTypeBadges } from '../utils/activityUtils';
import ActivityMap from './ActivityMap';
import RunningWorkoutViz from './training/RunningWorkoutViz';
import { cn } from '../lib/utils';
import {
  buildExerciseSplitRanges,
  buildLapRanges,
  buildStravaSplitRanges,
  exerciseIndexForKm,
  exerciseIndexForLap,
  kmMarkersForExerciseIndex,
  kmMarkersForLap,
  kmMarkersForStravaSplit,
  lapForExerciseIndex,
  lapForKm,
  stravaSplitForKm,
  rangeIndexForPositionM,
} from '../utils/activityKmLapLink';
import { computeWorkSplitWeightedAverages } from '../utils/workSplitLapAverages';
import {
  cpPercentHeatmapColor,
  endlessStyle,
  runningSegmentBarColorClass,
  runningSegmentBarCssColor,
  runningSegmentUsesCpHeatmapBarColor,
  segmentRowToVizSplit,
  vizSplitCpForHeatmap,
} from '../utils/runningWorkoutVizShared';

function formatDistanceKm(meters) {
  const m = parseFloat(meters);
  if (!Number.isFinite(m) || m <= 0) return '—';
  return `${(m / 1000).toFixed(2)} km`;
}

function formatPace(speed) {
  const sp = parseFloat(speed);
  if (!Number.isFinite(sp) || sp === 0) return '—';
  const paceSeconds = 1000 / sp;
  if (!Number.isFinite(paceSeconds)) return '—';
  const mins = Math.floor(paceSeconds / 60);
  const secs = Math.round(paceSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} /km`;
}

function formatElevation(meters) {
  const m = parseFloat(meters);
  if (!Number.isFinite(m)) return '—';
  return `${Math.round(m)} m`;
}

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatPaceSecPerKm(sec, t) {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}${t('units.perKm')}`;
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function lapIntervalClassTooltipLabel(ic, t) {
  const c = String(ic || 'work').toLowerCase();
  if (c === 'warmup') return t('activityDetail.draftIntensityWarmup');
  if (c === 'cooldown') return t('activityDetail.draftIntensityCooldown');
  if (c === 'rest') return t('activityDetail.draftIntensityRest');
  return t('activityDetail.draftIntensityWork');
}

/** Matches RunningWorkoutViz bar colors for linked planned segments. */
function LapIntervalTypeDot({
  lapIndex,
  lapLinkByIndex,
  exerciseSplits,
  fallbackSegmentIndex,
  plannedSegmentIndex,
  t,
}) {
  if (
    plannedSegmentIndex != null &&
    Array.isArray(exerciseSplits) &&
    exerciseSplits[plannedSegmentIndex] != null
  ) {
    const rawSeg = exerciseSplits[plannedSegmentIndex];
    const vizSplit = segmentRowToVizSplit(rawSeg);
    const ic = vizSplit.run_intensity_class || 'work';
    const heatmap = runningSegmentUsesCpHeatmapBarColor(ic);
    const dotColor = heatmap ? cpPercentHeatmapColor(vizSplitCpForHeatmap(vizSplit)) : runningSegmentBarCssColor(ic);
    return (
      <span
        className={cn(
          'inline-block h-2 w-2 shrink-0 rounded-full',
          !(heatmap || rawSeg.endless) && runningSegmentBarColorClass(ic)
        )}
        style={{
          ...(heatmap && !rawSeg.endless ? { backgroundColor: dotColor } : {}),
          ...(rawSeg.endless ? endlessStyle(dotColor) : {}),
        }}
        title={lapIntervalClassTooltipLabel(ic, t)}
        aria-hidden
      />
    );
  }

  const link = lapLinkByIndex?.get(Number(lapIndex));
  const fromLinkSeg =
    link && Array.isArray(exerciseSplits) ? exerciseSplits[link.segment_index] : null;
  const fromFallbackSeg =
    !link &&
    fallbackSegmentIndex != null &&
    Array.isArray(exerciseSplits) &&
    exerciseSplits[fallbackSegmentIndex] != null
      ? exerciseSplits[fallbackSegmentIndex]
      : null;
  const rawSeg = fromLinkSeg || fromFallbackSeg;

  if (!link && !rawSeg) {
    return (
      <span
        className='inline-block h-2 w-2 shrink-0 rounded-full bg-muted-foreground/25'
        aria-hidden
      />
    );
  }

  const vizSplit = rawSeg
    ? segmentRowToVizSplit(rawSeg)
    : {
        duration_sec: 0,
        split_measure_mode: 'duration',
        run_intensity_class: link.run_intensity_class,
        cp_percent: null,
        intensity_norm: undefined,
        distance_m: undefined,
        distance_display_unit: undefined,
      };

  const ic = vizSplit.run_intensity_class || 'work';
  const heatmap = runningSegmentUsesCpHeatmapBarColor(ic);
  const isEndless = rawSeg?.endless;
  const dotColor = heatmap ? cpPercentHeatmapColor(vizSplitCpForHeatmap(vizSplit)) : runningSegmentBarCssColor(ic);

  return (
    <span
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full',
        !(heatmap || isEndless) && runningSegmentBarColorClass(ic)
      )}
      style={{
        ...(heatmap && !isEndless ? { backgroundColor: dotColor } : {}),
        ...(isEndless ? endlessStyle(dotColor) : {}),
      }}
      title={lapIntervalClassTooltipLabel(ic, t)}
      aria-hidden
    />
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className='bg-card border border-border rounded-lg p-3 text-center'>
    <div className='flex justify-center mb-1.5 text-primary [&>svg]:h-4 [&>svg]:w-4'>{icon}</div>
    <p className='text-lg font-bold text-foreground'>{value}</p>
    <p className='text-xs text-muted-foreground'>{label}</p>
  </div>
);

function splitAverageWatts(split) {
  const w = split.average_watts != null ? parseFloat(split.average_watts) : NaN;
  return Number.isFinite(w) && w > 0 ? Math.round(w) : null;
}

function SplitBars({ splits, highlightedSplitIndices, onSplitRowHover, t }) {
  if (!splits || splits.length === 0) return null;

  const paces = splits
    .map((s) => (s.average_speed && parseFloat(s.average_speed) > 0 ? 1000 / parseFloat(s.average_speed) : null))
    .filter(Boolean);

  if (paces.length === 0) return null;

  const minPace = Math.min(...paces);
  const maxPace = Math.max(...paces);
  const range = maxPace - minPace || 1;
  const hiSet = highlightedSplitIndices instanceof Set ? highlightedSplitIndices : null;
  const dash = '-';
  const gridCols =
    'grid grid-cols-[1.5rem_minmax(0,1fr)_3rem_3.5rem_minmax(3.5rem,max-content)] gap-x-2 items-center text-xs';

  return (
    <div className='space-y-1'>
      <div className={`${gridCols} border-b border-border/60 pb-1 text-[0.7rem] sm:text-xs font-medium text-muted-foreground`}>
        <span aria-hidden className='text-right tabular-nums'>#</span>
        <span className='min-w-0' aria-hidden />
        <span className='text-right tabular-nums'>{t('activityDetail.pace')}</span>
        <span className='text-right tabular-nums'>{t('activityDetail.splitsColHr')}</span>
        <span className='text-right tabular-nums'>{t('activityDetail.avgPower')}</span>
      </div>
      {splits.map((split) => {
        const pace =
          split.average_speed && parseFloat(split.average_speed) > 0 ? 1000 / parseFloat(split.average_speed) : null;
        const pct = pace ? Math.max(20, 100 - ((pace - minPace) / range) * 70) : 20;
        const mins = pace ? Math.floor(pace / 60) : 0;
        const secs = pace ? Math.round(pace % 60) : 0;
        const paceLabel = pace ? `${mins}:${secs.toString().padStart(2, '0')}` : dash;
        const idx = Number(split.split_index);
        const rowHi = hiSet && hiSet.has(idx);
        const hrVal =
          split.average_heartrate && parseFloat(split.average_heartrate) > 0
            ? Math.round(parseFloat(split.average_heartrate))
            : null;
        const wattsVal = splitAverageWatts(split);

        return (
          <div
            key={split.split_index}
            className={cn(
              `${gridCols} rounded-md px-1 py-0.5 transition-colors`,
              rowHi && 'bg-amber-500/15 ring-1 ring-amber-500/60'
            )}
            onMouseEnter={() => onSplitRowHover?.(idx)}
            onMouseLeave={() => onSplitRowHover?.(null)}
          >
            <span className='text-right text-muted-foreground tabular-nums'>{split.split_index}</span>
            <div
              className='min-h-5 min-w-0 overflow-hidden rounded bg-muted/30'
              aria-hidden
              title={t('activityDetail.splitsBarAria')}
            >
              <div
                className='h-5 max-w-full rounded bg-primary/70 transition-all'
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className='text-right font-medium tabular-nums text-foreground'>{paceLabel}</span>
            <span className='text-right tabular-nums text-muted-foreground'>
              {hrVal != null ? `${hrVal} ${t('units.bpm')}` : dash}
            </span>
            <span className='text-right tabular-nums text-muted-foreground'>
              {wattsVal != null ? `${wattsVal} ${t('units.powerW')}` : dash}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function deriveKmLapSplitHighlights(hover, lapRanges, exRanges, stravaSplitRanges) {
  const empty = {
    kmSet: new Set(),
    lapIndex: null,
    exIdx: null,
    splitIndices: new Set(),
  };
  if (!hover || !hover.type) return empty;

  if (hover.type === 'km') {
    const km = hover.km;
    const kms = new Set([km]);
    const lapHit = lapForKm(km, lapRanges);
    const exHit = exerciseIndexForKm(km, exRanges);
    const si = stravaSplitForKm(km, stravaSplitRanges);
    return {
      kmSet: kms,
      lapIndex: lapHit,
      exIdx: exHit,
      splitIndices: si != null ? new Set([si]) : new Set(),
    };
  }

  if (hover.type === 'lap') {
    const kms = new Set(kmMarkersForLap(hover.lapIndex, lapRanges));
    const exHit = exerciseIndexForLap(hover.lapIndex, lapRanges, exRanges);
    const sis = new Set();
    kms.forEach((k) => {
      const si = stravaSplitForKm(k, stravaSplitRanges);
      if (si != null) sis.add(si);
    });
    return { kmSet: kms, lapIndex: hover.lapIndex, exIdx: exHit, splitIndices: sis };
  }

  if (hover.type === 'ex') {
    const kms = new Set(kmMarkersForExerciseIndex(hover.idx, exRanges));
    const lapHit = lapForExerciseIndex(hover.idx, exRanges, lapRanges);
    const sis = new Set();
    kms.forEach((k) => {
      const si = stravaSplitForKm(k, stravaSplitRanges);
      if (si != null) sis.add(si);
    });
    return { kmSet: kms, lapIndex: lapHit, exIdx: hover.idx, splitIndices: sis };
  }

  if (hover.type === 'strava') {
    const kms = new Set(kmMarkersForStravaSplit(hover.splitIndex, stravaSplitRanges));
    const r = stravaSplitRanges.find((x) => x.splitIndex === hover.splitIndex);
    let lapHit = null;
    let exIdx = null;
    if (r) {
      const mid = (r.startM + r.endM) / 2;
      const lr = rangeIndexForPositionM(mid, lapRanges);
      lapHit = lr ? lr.lapIndex : null;
      const er = rangeIndexForPositionM(mid, exRanges);
      exIdx = er ? er.index : null;
    }
    return {
      kmSet: kms,
      lapIndex: lapHit,
      exIdx,
      splitIndices: new Set([hover.splitIndex]),
    };
  }

  return empty;
}

/**
 * Large preview of a Strava activity (same API as Activity detail), stacked above KPI trend modal.
 */
export default function ActivityPeekModal({ activityId, onClose }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const idNum = activityId != null ? Number(activityId) : NaN;
  const open = Number.isFinite(idNum) && idNum > 0;

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [splitUnit, setSplitUnit] = useState('metric');
  const [lapsPeekViewSource, setLapsPeekViewSource] = useState('device');
  const [linkHover, setLinkHover] = useState(null);
  const hoverClearRef = useRef(null);

  const beginHover = useCallback((payload) => {
    if (hoverClearRef.current) {
      clearTimeout(hoverClearRef.current);
      hoverClearRef.current = null;
    }
    setLinkHover(payload);
  }, []);

  const endHover = useCallback(() => {
    hoverClearRef.current = setTimeout(() => {
      setLinkHover(null);
      hoverClearRef.current = null;
    }, 70);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverClearRef.current) clearTimeout(hoverClearRef.current);
    };
  }, []);

  useEffect(() => {
    setSplitUnit('metric');
    setLinkHover(null);
    setLapsPeekViewSource('device');
  }, [idNum]);

  useEffect(() => {
    const pl = payload?.planned_laps;
    if (Array.isArray(pl) && pl.length > 0) {
      setLapsPeekViewSource('workout');
    }
  }, [payload?.planned_laps]);

  useEffect(() => {
    if (!open) {
      setPayload(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setPayload(null);
      try {
        const data = await fetchStravaActivityWithStreams(idNum);
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('kpiTrend.activityPeekLoadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, idNum, t]);

  const activity = payload?.activity;
  const laps = payload?.laps || [];
  const plannedLaps = payload?.planned_laps;
  const hasPlannedLapsView = Array.isArray(plannedLaps) && plannedLaps.length > 0;
  const displayLaps =
    hasPlannedLapsView && lapsPeekViewSource === 'workout' ? plannedLaps : laps;
  const streams = payload?.streams || {};
  const splitsPayload = payload?.splits || { metric: [], standard: [] };
  const linkedRunning = payload?.linked_running_exercise;
  const exerciseSplits = Array.isArray(linkedRunning?.preview_splits) ? linkedRunning.preview_splits : [];
  const lapSegmentLinks = linkedRunning?.lap_segment_links;
  const kmMetric = splitsPayload.metric || [];
  const kmStandard = splitsPayload.standard || [];
  const hasKmSplits = kmMetric.length > 0 || kmStandard.length > 0;
  const showExerciseKmTabs = exerciseSplits.length > 0 && hasKmSplits;
  const showExerciseSplitsOnly = exerciseSplits.length > 0 && !hasKmSplits;
  const hasMap = !!(activity?.polyline || activity?.summary_polyline);
  const hasSplitsSection = showExerciseKmTabs || showExerciseSplitsOnly;
  const hasLapsSection = laps.length > 0;
  const hasDetailsRow = hasMap || hasSplitsSection || hasLapsSection;

  const activityDistanceM = useMemo(
    () => Math.max(0, parseFloat(activity?.distance) || 0),
    [activity?.distance]
  );

  const lapRanges = useMemo(() => buildLapRanges(displayLaps), [displayLaps]);

  const exRanges = useMemo(
    () => buildExerciseSplitRanges(exerciseSplits, activityDistanceM),
    [exerciseSplits, activityDistanceM]
  );

  const metricStravaRanges = useMemo(() => buildStravaSplitRanges(kmMetric, 'metric'), [kmMetric]);

  const standardStravaRanges = useMemo(() => buildStravaSplitRanges(kmStandard, 'standard'), [kmStandard]);

  const stravaRangesForLink = splitUnit === 'standard' ? standardStravaRanges : metricStravaRanges;

  const highlightBundle = useMemo(
    () => deriveKmLapSplitHighlights(linkHover, lapRanges, exRanges, stravaRangesForLink),
    [linkHover, lapRanges, exRanges, stravaRangesForLink]
  );

  const workSplitAverages = useMemo(
    () => computeWorkSplitWeightedAverages(exerciseSplits, activityDistanceM, laps, lapSegmentLinks),
    [exerciseSplits, activityDistanceM, laps, lapSegmentLinks]
  );

  const lapLinkByLapIndex = useMemo(() => {
    const m = new Map();
    if (Array.isArray(lapSegmentLinks)) {
      lapSegmentLinks.forEach((l) => {
        m.set(Number(l.lap_index), l);
      });
    }
    return m;
  }, [lapSegmentLinks]);

  const onKmMarkerHover = useCallback(
    (km) => {
      if (km != null) {
        beginHover({ type: 'km', km });
      } else {
        endHover();
      }
    },
    [beginHover, endHover]
  );

  const handleOpenFull = () => {
    onClose();
    navigate(`/activity/${idNum}`);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton
        className={cn(
          'z-[10250] flex max-h-[90vh] w-[90vw] max-w-[90vw] flex-col gap-0 overflow-hidden p-0',
          'translate-x-[-50%] translate-y-[-50%]'
        )}
        overlayClassName='z-[10240]'
      >
        <DialogHeader className='shrink-0 space-y-0 border-b border-border px-6 py-4 text-left'>
          <div className='flex flex-wrap items-start justify-between gap-3 pr-8'>
            <div className='min-w-0 flex-1'>
              <DialogTitle className='text-left text-base font-semibold leading-snug'>
                {loading ? '…' : activity?.name || t('kpiTrend.activityPeekTitle')}
              </DialogTitle>
              {!!activity && (
                <div className='mt-2 flex flex-wrap items-center gap-1.5'>
                  {(() => {
                    const { mainLabel, mainColor, extraBadges } = getActivityTypeBadges(activity, t);
                    return (
                      <>
                        <Badge variant='outline' className={mainColor}>
                          {mainLabel}
                        </Badge>
                        {extraBadges.map((b) => (
                          <Badge key={b.label} variant='outline' className={`text-xs ${b.className}`}>
                            {b.label}
                          </Badge>
                        ))}
                      </>
                    );
                  })()}
                </div>
              )}
              {!!activity && (
                <p className='mt-2 text-xs text-muted-foreground'>
                  {formatFullDate(activity.start_date_local || activity.start_date)}
                  {activity.timezone && ` · ${activity.timezone}`}
                </p>
              )}
            </div>
            {open && !loading && !error && activity && (
              <Button type='button' variant='outline' size='sm' className='shrink-0' onClick={handleOpenFull}>
                <ExternalLink className='mr-1.5 h-3.5 w-3.5' />
                {t('kpiTrend.openFullActivity')}
              </Button>
            )}
          </div>
        </DialogHeader>

        <div
          className={cn(
            'min-h-0 flex flex-1 flex-col px-6 py-4',
            loading || error ? 'overflow-y-auto' : 'overflow-hidden'
          )}
        >
          {loading && (
            <div className='flex justify-center py-16'>
              <Loader2 className='h-10 w-10 animate-spin text-muted-foreground' />
            </div>
          )}
          {error && !loading && (
            <p className='text-center text-sm text-destructive'>{error}</p>
          )}
          {!loading && !error && activity && (
            <>
              <div className='mb-4 shrink-0 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-7'>
                {(() => {
                  const m = parseFloat(activity.distance);
                  return Number.isFinite(m) && m > 0 ? (
                    <StatCard icon={<MapPin />} label={t('athlete.distance')} value={formatDistanceKm(activity.distance)} />
                  ) : null;
                })()}
                {(() => {
                  const mt = parseFloat(activity.moving_time);
                  return Number.isFinite(mt) && mt > 0 ? (
                    <StatCard icon={<Clock />} label={t('athlete.duration')} value={formatDuration(activity.moving_time)} />
                  ) : null;
                })()}
                {(() => {
                  const sp = parseFloat(activity.average_speed);
                  return Number.isFinite(sp) && sp > 0 ? (
                    <StatCard icon={<Gauge />} label={t('athlete.pace')} value={formatPace(activity.average_speed)} />
                  ) : null;
                })()}
                {(() => {
                  const eg = parseFloat(activity.total_elevation_gain);
                  return Number.isFinite(eg) && eg > 0 ? (
                    <StatCard icon={<Mountain />} label={t('athlete.elevation')} value={formatElevation(activity.total_elevation_gain)} />
                  ) : null;
                })()}
                {(() => {
                  const hr = parseFloat(activity.average_heartrate);
                  return activity.has_heartrate && Number.isFinite(hr) && hr > 0 ? (
                    <StatCard icon={<Heart />} label={t('athlete.heartRate')} value={`${Math.round(hr)} bpm`} />
                  ) : null;
                })()}
                {(() => {
                  const c = parseFloat(activity.calories);
                  return Number.isFinite(c) && c > 0 ? (
                    <StatCard icon={<Flame />} label={t('athlete.calories')} value={`${Math.round(c)}`} />
                  ) : null;
                })()}
                {(() => {
                  const s = parseFloat(activity.suffer_score);
                  return Number.isFinite(s) && s > 0 ? (
                    <StatCard icon={<HeartPulse />} label={t('activityDetail.sufferScore')} value={String(Math.round(s))} />
                  ) : null;
                })()}
              </div>

              {hasDetailsRow && (
                <div className='flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6'>
                  {hasMap && (
                    <div className='flex min-h-0 w-full flex-1 flex-col lg:min-w-0 lg:basis-1/2'>
                      <h2 className='mb-2 flex shrink-0 items-center gap-2 text-lg font-semibold text-foreground'>
                        <MapPin className='h-5 w-5 text-primary' />
                        {t('activityDetail.route')}
                      </h2>
                      <div className='min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-card'>
                        <ActivityMap
                          encodedPolyline={activity.polyline}
                          summaryPolyline={activity.summary_polyline}
                          streams={streams}
                          fillHeight
                          fitPadding={[8, 8]}
                          className='h-full rounded-lg border-0'
                          highlightKmSet={highlightBundle.kmSet}
                          onKmMarkerHover={onKmMarkerHover}
                        />
                      </div>
                    </div>
                  )}

                  {(hasSplitsSection || hasLapsSection) && (
                    <div
                      className={cn(
                        'flex min-h-0 w-full flex-1 flex-col lg:min-w-0 lg:basis-1/2',
                        !hasMap && 'lg:basis-full'
                      )}
                    >
                      <div className='min-h-0 flex-1 space-y-6 overflow-y-auto pr-4'>
                        {showExerciseKmTabs && (
                          <div>
                            <Tabs defaultValue='exercise' key={idNum} className='w-full'>
                              <TabsList className='h-9 w-full justify-start sm:w-auto'>
                                <TabsTrigger value='exercise' className='text-xs sm:text-sm'>
                                  {t('activityDetail.exerciseSplits')}
                                </TabsTrigger>
                                <TabsTrigger value='km' className='text-xs sm:text-sm'>
                                  {t('activityDetail.kmSplitsTab')}
                                </TabsTrigger>
                              </TabsList>
                              <TabsContent value='exercise' className='mt-3'>
                                <RunningWorkoutViz
                                  splits={exerciseSplits}
                                  heightPx={52}
                                  highlightedBarIndex={highlightBundle.exIdx}
                                  onBarHover={(idx) =>
                                    idx == null ? endHover() : beginHover({ type: 'ex', idx })
                                  }
                                />
                              </TabsContent>
                              <TabsContent value='km' className='mt-3'>
                                <div className='mb-3 flex gap-1'>
                                  <Button
                                    type='button'
                                    variant={splitUnit === 'metric' ? 'default' : 'outline'}
                                    size='sm'
                                    className='h-7 text-xs'
                                    onClick={() => setSplitUnit('metric')}
                                  >
                                    km
                                  </Button>
                                  <Button
                                    type='button'
                                    variant={splitUnit === 'standard' ? 'default' : 'outline'}
                                    size='sm'
                                    className='h-7 text-xs'
                                    onClick={() => setSplitUnit('standard')}
                                  >
                                    mi
                                  </Button>
                                </div>
                                <SplitBars
                                  splits={splitUnit === 'standard' ? kmStandard : kmMetric}
                                  highlightedSplitIndices={highlightBundle.splitIndices}
                                  onSplitRowHover={(splitIndex) =>
                                    splitIndex == null
                                      ? endHover()
                                      : beginHover({ type: 'strava', splitIndex })
                                  }
                                  t={t}
                                />
                              </TabsContent>
                            </Tabs>
                          </div>
                        )}

                        {showExerciseSplitsOnly && (
                          <div>
                            <h2 className='mb-3 flex items-center gap-2 text-lg font-semibold text-foreground'>
                              <Activity className='h-5 w-5 text-primary' />
                              {t('activityDetail.exerciseSplits')}
                            </h2>
                            <RunningWorkoutViz
                              splits={exerciseSplits}
                              heightPx={52}
                              highlightedBarIndex={highlightBundle.exIdx}
                              onBarHover={(idx) =>
                                idx == null ? endHover() : beginHover({ type: 'ex', idx })
                              }
                            />
                          </div>
                        )}

                        {hasLapsSection && (
                          <div>
                            <div className='mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
                              <h2 className='flex min-w-0 items-center gap-2 text-lg font-semibold text-foreground'>
                                <Timer className='h-5 w-5 shrink-0 text-primary' />
                                <span className='truncate'>
                                  {t('activityDetail.laps')} ({displayLaps.length})
                                </span>
                              </h2>
                              {hasPlannedLapsView && (
                                <div className='flex flex-wrap items-center gap-2'>
                                  <div className='flex items-center gap-1 rounded-md border border-border p-0.5 bg-muted/30'>
                                    <Button
                                      type='button'
                                      variant={lapsPeekViewSource === 'workout' ? 'default' : 'ghost'}
                                      size='sm'
                                      className='h-7 text-xs'
                                      onClick={() => setLapsPeekViewSource('workout')}
                                    >
                                      {t('activityDetail.lapsViewWorkoutLaps')}
                                    </Button>
                                    <Button
                                      type='button'
                                      variant={lapsPeekViewSource === 'device' ? 'default' : 'ghost'}
                                      size='sm'
                                      className='h-7 text-xs'
                                      onClick={() => setLapsPeekViewSource('device')}
                                    >
                                      {t('activityDetail.lapsViewDeviceLaps')} ({laps.length})
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {workSplitAverages &&
                                (workSplitAverages.paceSecPerKm != null ||
                                  workSplitAverages.avgHr != null ||
                                  workSplitAverages.avgWatts != null) && (
                                  <div
                                    className='flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5 text-xs text-muted-foreground'
                                    role='group'
                                    aria-label={t('activityDetail.workPhaseAveragesAria')}
                                  >
                                    {workSplitAverages.paceSecPerKm != null && (
                                      <span className='inline-flex items-center gap-1'>
                                        <TrendingUp className='h-3 w-3 shrink-0' aria-hidden />
                                        <span>{formatPaceSecPerKm(workSplitAverages.paceSecPerKm, t)}</span>
                                      </span>
                                    )}
                                    {workSplitAverages.avgHr != null && (
                                      <span className='inline-flex items-center gap-1'>
                                        <Heart className='h-3 w-3 shrink-0' aria-hidden />
                                        <span>
                                          {Math.round(workSplitAverages.avgHr)} {t('units.bpm')}
                                        </span>
                                      </span>
                                    )}
                                    {workSplitAverages.avgWatts != null && (
                                      <span className='inline-flex items-center gap-1'>
                                        <Zap className='h-3 w-3 shrink-0' aria-hidden />
                                        <span>
                                          {Math.round(workSplitAverages.avgWatts)} {t('units.powerW')}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                )}
                            </div>
                            {hasPlannedLapsView && lapsPeekViewSource === 'workout' && (
                              <p className='mb-2 text-xs text-muted-foreground'>{t('activityDetail.lapsAlignedHint')}</p>
                            )}
                            <div className='overflow-x-auto'>
                              <table className='w-full text-sm'>
                                <thead>
                                  <tr className='border-b border-border text-muted-foreground'>
                                    <th className='w-7 py-2 pl-0 pr-1' aria-hidden />
                                    <th className='py-2 pr-4 text-left font-medium'>#</th>
                                    <th className='px-2 py-2 text-right font-medium'>{t('athlete.distance')}</th>
                                    <th className='px-2 py-2 text-right font-medium'>{t('athlete.duration')}</th>
                                    <th className='px-2 py-2 text-right font-medium'>{t('athlete.pace')}</th>
                                    <th className='px-2 py-2 text-right font-medium'>{t('athlete.heartRate')}</th>
                                    <th className='px-2 py-2 text-right font-medium'>{t('activityDetail.avgPower')}</th>
                                    <th className='pl-2 py-2 text-right font-medium'>{t('athlete.elevation')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayLaps.slice(0, 12).map((lap) => (
                                    <tr
                                      key={lap.id ?? `lap-${lap.lap_index}`}
                                      className={cn(
                                        'border-b border-border/50 transition-colors',
                                        Number(highlightBundle.lapIndex) === Number(lap.lap_index) &&
                                          'bg-amber-500/15 ring-1 ring-inset ring-amber-500/50'
                                      )}
                                      onMouseEnter={() =>
                                        beginHover({ type: 'lap', lapIndex: lap.lap_index })
                                      }
                                      onMouseLeave={endHover}
                                    >
                                      <td className='py-2 pl-0 pr-1 align-middle'>
                                        <LapIntervalTypeDot
                                          lapIndex={lap.lap_index}
                                          lapLinkByIndex={lapLinkByLapIndex}
                                          exerciseSplits={exerciseSplits}
                                          plannedSegmentIndex={
                                            hasPlannedLapsView &&
                                            lapsPeekViewSource === 'workout' &&
                                            lap.segment_index != null
                                              ? lap.segment_index
                                              : undefined
                                          }
                                          fallbackSegmentIndex={exerciseIndexForLap(
                                            lap.lap_index,
                                            lapRanges,
                                            exRanges
                                          )}
                                          t={t}
                                        />
                                      </td>
                                      <td className='py-2 pr-4 text-muted-foreground'>{lap.lap_index}</td>
                                      <td className='px-2 py-2 text-right'>{formatDistanceKm(lap.distance)}</td>
                                      <td className='px-2 py-2 text-right'>{formatDuration(lap.moving_time)}</td>
                                      <td className='px-2 py-2 text-right'>{formatPace(lap.average_speed)}</td>
                                      <td className='px-2 py-2 text-right'>
                                        {lap.average_heartrate ? `${Math.round(parseFloat(lap.average_heartrate))}` : '—'}
                                      </td>
                                      <td className='px-2 py-2 text-right'>
                                        {lap.average_watts != null &&
                                        Number.isFinite(parseFloat(lap.average_watts)) &&
                                        parseFloat(lap.average_watts) > 0
                                          ? `${Math.round(parseFloat(lap.average_watts))} ${t('units.powerW')}`
                                          : '—'}
                                      </td>
                                      <td className='pl-2 py-2 text-right'>{formatElevation(lap.total_elevation_gain)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {displayLaps.length > 12 && (
                              <p className='mt-2 text-center text-xs text-muted-foreground'>
                                {t('kpiTrend.activityPeekMoreLaps')}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
